/**
 * Component: Hudson location
 * The shopper's chosen location: where it is stored, how a ZIP or a browser position turns into
 * a place, and which showroom is nearest to it.
 *
 * DOM-free on purpose. The header selector is only one consumer — the PDP showroom row reads it
 * too, and it renders on pages where the header group is hidden (settings.embed_page_templates),
 * so the state cannot live inside the header element.
 *
 * Loaded as a classic deferred script from the <head>, before every consumer: deferred scripts
 * run in document order, so window.HudsonLocation always exists by the time they do. State is
 * resolved synchronously here — no network on boot — which is what lets get() be valid the
 * instant this file executes and removes the whole class of "who loaded first" bugs. Consumers
 * read get() in connectedCallback and subscribe to EVENT for everything after.
 *
 */
window.HudsonLocation =
  window.HudsonLocation ||
  (() => {
    const LABEL = "[location]";
    const CONFIG_SELECTOR = "[data-hudson-location-config]";

    const STORAGE_KEY = "hudson:location";
    const CHAT_KEY = "hudson:chat-location";
    const PROMPTED_KEY = "hudson:location-prompted";
    const EVENT = "hudson:location-change";
    const PAYLOAD_VERSION = 1;
    const LOCATION_PARAM = "loc";

    const ZIP_PATTERN = /^(\d{5})(?:-\d{4})?$/;
    const EARTH_RADIUS_MILES = 3958.8;
    const DEFAULT_MAX_SHOWROOM_MILES = 150;

    const GEOLOCATION_TIMEOUT_MS = 10000;
    const POSITION_MAX_AGE_MS = 300000;

    /* The geocoding chain is its own file; it owns the error vocabulary because it raises most
       of it. Re-exported below so consumers only ever see window.HudsonLocation. */
    const providerLayer = window.HudsonLocationProviders;
    if (!providerLayer) {
      console.error("[location] location-providers.js did not load, ZIP lookup is disabled.");
    }

    const LocationError =
      providerLayer?.LocationError ||
      class extends Error {
        constructor(code) {
          super(code);
          this.name = "LocationError";
          this.code = code;
        }
      };
    const ERROR_CODES = providerLayer?.ERROR_CODES || Object.freeze({ NETWORK: "NETWORK" });

    function readConfig() {
      const node = document.querySelector(CONFIG_SELECTOR);
      if (!node) return {};

      try {
        return JSON.parse(node.textContent) || {};
      } catch (error) {
        console.error(`${LABEL} configuration is not valid JSON, showrooms are unavailable.`, error);
        return {};
      }
    }

    const config = readConfig();
    const apiKey = typeof config.apiKey === "string" ? config.apiKey.trim() : "";
    const maxShowroomMiles = Number(config.maxShowroomMiles) || DEFAULT_MAX_SHOWROOM_MILES;

    const geocoder = providerLayer
      ? providerLayer.create({ apiKey })
      : {
          lookupZip: () => Promise.reject(new LocationError(ERROR_CODES.NETWORK)),
          reverse: () => Promise.reject(new LocationError(ERROR_CODES.NETWORK)),
        };

    /* Number(null) is 0, so an entry with no latitude would sit at 0°,0° and win every distance
       comparison from the Atlantic. Anything blank has to become NaN for the filter below. */
    const toCoordinate = (value) =>
      value === null || value === undefined || value === "" ? NaN : Number(value);

    const showrooms = Object.freeze(
      (Array.isArray(config.showrooms) ? config.showrooms : [])
        .map((entry) => ({
          id: String(entry.id),
          name: entry.name || "",
          lat: toCoordinate(entry.lat),
          lng: toCoordinate(entry.lng),
        }))
        .filter((entry) => entry.id !== ""),
    );

    const locatableShowrooms = showrooms.filter(
      (entry) => Number.isFinite(entry.lat) && Number.isFinite(entry.lng),
    );

    const toRadians = (degrees) => (degrees * Math.PI) / 180;

    function distanceInMiles(fromLat, fromLng, toLat, toLng) {
      const deltaLat = toRadians(toLat - fromLat);
      const deltaLng = toRadians(toLng - fromLng);
      const a =
        Math.sin(deltaLat / 2) ** 2 +
        Math.cos(toRadians(fromLat)) * Math.cos(toRadians(toLat)) * Math.sin(deltaLng / 2) ** 2;

      return EARTH_RADIUS_MILES * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    function nearest(lat, lng) {
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !locatableShowrooms.length) return null;

      return locatableShowrooms.reduce((closest, showroom) => {
        const distanceMi = distanceInMiles(lat, lng, showroom.lat, showroom.lng);
        return !closest || distanceMi < closest.distanceMi ? { showroom, distanceMi } : closest;
      }, null);
    }

    let storageWorks = true;
    const memoryStorage = {};

    function readRaw(key) {
      if (!storageWorks) return memoryStorage[key] ?? null;

      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        storageWorks = false;
        return memoryStorage[key] ?? null;
      }
    }

    function writeRaw(key, value) {
      memoryStorage[key] = value;
      if (!storageWorks) return;

      try {
        window.sessionStorage.setItem(key, value);
      } catch (error) {
        storageWorks = false;
      }
    }

    function removeRaw(key) {
      delete memoryStorage[key];
      if (!storageWorks) return;

      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
        storageWorks = false;
      }
    }

    function readStored() {
      const raw = readRaw(STORAGE_KEY);
      if (!raw) return null;

      try {
        const payload = JSON.parse(raw);
        if (!payload || payload.v !== PAYLOAD_VERSION) {
          removeRaw(STORAGE_KEY);
          return null;
        }
        return payload;
      } catch (error) {
        removeRaw(STORAGE_KEY);
        return null;
      }
    }

    function format(place) {
      if (!place) return "";

      const cityState = [place.city, place.region].filter(Boolean).join(", ");
      return [cityState, place.zip].filter(Boolean).join(" ");
    }

    let current = null;

    function persist(payload, source) {
      const previous = current;
      current = payload;

      if (payload) {
        writeRaw(STORAGE_KEY, JSON.stringify(payload));
      } else {
        removeRaw(STORAGE_KEY);
      }

      if (payload?.showroomId) {
        writeRaw(CHAT_KEY, payload.showroomId);
      } else {
        removeRaw(CHAT_KEY);
      }

      document.dispatchEvent(
        new CustomEvent(EVENT, { detail: { location: payload, previous, source } }),
      );

      return payload;
    }

    function buildPayload(place, source) {
      const match = nearest(place.lat, place.lng);
      const withinRange = match && match.distanceMi <= maxShowroomMiles;

      return {
        v: PAYLOAD_VERSION,
        zip: place.zip,
        city: place.city,
        region: place.region,
        label: format(place),
        lat: Number.isFinite(place.lat) ? place.lat : null,
        lng: Number.isFinite(place.lng) ? place.lng : null,
        showroomId: withinRange ? match.showroom.id : null,
        distanceMi: match ? Math.round(match.distanceMi * 10) / 10 : null,
        source,
        ts: Date.now(),
      };
    }

    const EMPTY_PLACE = Object.freeze({ zip: "", city: "", region: "", lat: NaN, lng: NaN });
    const blankPayload = (source) => buildPayload(EMPTY_PLACE, source);

    const findShowroom = (id) =>
      id ? showrooms.find((showroom) => showroom.id === String(id)) || null : null;

    (function resolve() {
      const stored = readStored();
      const requestedId = new URLSearchParams(window.location.search).get(LOCATION_PARAM);
      const requested = findShowroom(requestedId);

      if (requestedId && !requested) {
        console.warn(`${LABEL} no showroom with Location ID "${requestedId}", ignoring ?loc=.`);
      }

      if (requested) {
        current = stored
          ? { ...stored, showroomId: requested.id, source: "loc-param", ts: Date.now() }
          : { ...blankPayload("loc-param"), showroomId: requested.id };
      } else if (stored) {
        current = stored;
      } else {
        const legacy = findShowroom(readRaw(CHAT_KEY));
        current = legacy ? { ...blankPayload("showroom"), showroomId: legacy.id } : null;
      }

      if (current?.showroomId) {
        writeRaw(CHAT_KEY, current.showroomId);
        writeRaw(STORAGE_KEY, JSON.stringify(current));
      }
    })();

    let pending = null;
    let requestId = 0;

    function startRequest() {
      pending?.abort();
      pending = new AbortController();
      requestId += 1;
      return { signal: pending.signal, id: requestId };
    }

    const isCurrentRequest = (id) => id === requestId;

    function normalizeZip(value) {
      const match = String(value || "").trim().match(ZIP_PATTERN);
      if (!match) throw new LocationError(ERROR_CODES.INVALID_ZIP);
      return match[1];
    }

    async function setZip(value) {
      const zip = normalizeZip(value);
      const { signal, id } = startRequest();

      const place = await geocoder.lookupZip(zip, signal);

      if (!isCurrentRequest(id)) return current;
      return persist(buildPayload(place, "zip"), "zip");
    }

    function requestPosition() {
      if (!window.isSecureContext) throw new LocationError(ERROR_CODES.INSECURE_CONTEXT);
      if (!navigator.geolocation) throw new LocationError(ERROR_CODES.UNSUPPORTED);

      return new Promise((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new LocationError(ERROR_CODES.TIMEOUT)),
          GEOLOCATION_TIMEOUT_MS,
        );

        navigator.geolocation.getCurrentPosition(
          (position) => {
            clearTimeout(timer);
            resolve(position);
          },
          (error) => {
            clearTimeout(timer);
            reject(
              new LocationError(
                error.code === error.PERMISSION_DENIED
                  ? ERROR_CODES.PERMISSION_DENIED
                  : ERROR_CODES.POSITION_UNAVAILABLE,
              ),
            );
          },
          {
            enableHighAccuracy: false,
            timeout: GEOLOCATION_TIMEOUT_MS,
            maximumAge: POSITION_MAX_AGE_MS,
          },
        );
      });
    }

    async function useCurrentPosition() {
      const position = await requestPosition();
      const { latitude, longitude } = position.coords;
      const { signal, id } = startRequest();

      const place = await geocoder.reverse(latitude, longitude, signal);

      if (!isCurrentRequest(id)) return current;
      return persist(buildPayload(place, "geolocation"), "geolocation");
    }

    function readPrompted() {
      try {
        return window.localStorage.getItem(PROMPTED_KEY) === "1";
      } catch (error) {
        return false;
      }
    }

    function markPrompted() {
      try {
        window.localStorage.setItem(PROMPTED_KEY, "1");
      } catch (error) {
      }
    }

    async function permissionState() {
      if (!navigator.permissions?.query) return "unsupported";

      try {
        const status = await navigator.permissions.query({ name: "geolocation" });
        return status.state;
      } catch (error) {
        return "unsupported";
      }
    }

    /* Returns a payload only when a detection actually ran. A caller that wants the stored value
       has get(); returning it here made the header toast "Your location has been set to …" on
       every page load for the rest of the session. */
    async function autoDetect({ allowPrompt = true } = {}) {
      if (current) return null;
      if (!window.isSecureContext || !navigator.geolocation) return null;

      const state = await permissionState();
      if (state === "denied") return null;

      if (state !== "granted") {
        if (!allowPrompt || readPrompted()) return null;
        markPrompted();
      }

      try {
        return await useCurrentPosition();
      } catch (error) {
        return null;
      }
    }

    function setShowroomId(id) {
      const showroom = findShowroom(id);
      if (!showroom) throw new LocationError(ERROR_CODES.NO_SHOWROOM);

      const base = current || blankPayload("showroom");
      return persist({ ...base, showroomId: showroom.id, source: "showroom", ts: Date.now() }, "showroom");
    }

    function adoptShowroomId(id) {
      const showroom = findShowroom(id);
      if (!showroom || current?.showroomId === showroom.id) return current;

      const base = current || blankPayload("loc-param");
      return persist({ ...base, showroomId: showroom.id, source: "loc-param", ts: Date.now() }, "loc-param");
    }

    return Object.freeze({
      STORAGE_KEY,
      CHAT_KEY,
      EVENT,
      PAYLOAD_VERSION,
      ERROR_CODES,
      LocationError,
      showrooms,
      maxShowroomMiles,

      get: () => current,
      getShowroomId: () => current?.showroomId || null,
      getShowroom: () => findShowroom(current?.showroomId),
      format,
      nearest,

      setZip,
      useCurrentPosition,
      autoDetect,
      setShowroomId,
      adoptShowroomId,
      clear: () => persist(null, "clear"),
    });
  })();
